import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Stack,
  Divider,
  LinearProgress,
  Button,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  QrCode as QrCodeIcon,
  FlagOutlined as StatusIcon,
  CategoryOutlined as MaterialIcon,
  SensorsOutlined as DeviceIcon,
  ScaleOutlined as CurrentIcon,
  Inventory2Outlined as CapacityIcon,
  SpeedOutlined as FillIcon,
  TuneOutlined as BufferIcon,
  LocationOnOutlined as LocationIcon,
  LocationOffOutlined as LocationOffIcon,
  EventOutlined as DateIcon,
  LocalFireDepartmentOutlined as FireIcon,
  BatteryAlertOutlined as BatteryIcon,
  WarningAmberOutlined as FullIcon,
  ScreenRotationAltOutlined as TiltIcon,
} from '@mui/icons-material';
import EntityDetailRow from '../common/EntityDetailRow';
import { SetupCoachmark } from '../selfSetup/SetupCoachmark';
import { ContainerSchema, ContainerStatus } from '../../types/container.type';
import { calculateFillRate } from '../../helpers/container.helpers';
import { CONTAINER_STATUS_COLORS, STATUS_NEUTRAL, statusChipSx } from '../../constants/status-colors';

const STATUS_LABEL_KEYS: Record<ContainerStatus, string> = {
  [ContainerStatus.PENDING]: 'containers.status.pending',
  [ContainerStatus.EMPTY]: 'containers.status.empty',
  [ContainerStatus.AWAITING_PICKUP]: 'containers.status.awaitingPickup',
  [ContainerStatus.READY_FOR_PICKUP]: 'containers.status.readyForPickup',
  [ContainerStatus.MAINTENANCE]: 'containers.status.maintenance',
  [ContainerStatus.INACTIVE]: 'containers.status.inactive',
};

export interface ContainerMapDetailPanelProps {
  container: ContainerSchema;
  onClose: () => void;
  onEdit: (container: ContainerSchema) => void;
  onDelete: (id: string) => void;
  onQr: (container: ContainerSchema) => void;
  /** When true, fill a positioned parent instead of self-positioning. */
  embedded?: boolean;
  /** Knowledge Base "Go to Edit or delete" / print QR tips. */
  helpRowActionsPhase?: 'edit' | 'delete' | null;
  helpQrHint?: boolean;
  onHelpRowActionsNext?: () => void;
  onHelpRowActionsDismiss?: () => void;
  onHelpQrDismiss?: () => void;
}

const ContainerMapDetailPanel: React.FC<ContainerMapDetailPanelProps> = ({
  container,
  onClose,
  onEdit,
  onDelete,
  onQr,
  embedded = false,
  helpRowActionsPhase = null,
  helpQrHint = false,
  onHelpRowActionsNext,
  onHelpRowActionsDismiss,
  onHelpQrDismiss,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const displayName =
    container.data?.name || t('containers.map.containerFallback', { id: container.id.slice(0, 8) });
  const fillLevelKg = container.fill_level;
  const capacity = container.data?.capacity;
  const capacityUnit = container.data?.capacity_unit;
  const fillRate = calculateFillRate(fillLevelKg, capacity, capacityUnit);
  const statusColor = CONTAINER_STATUS_COLORS[container.status] ?? theme.palette.grey[500];
  const locationLabel = container.data?.location_name?.trim();
  const hasCoords = container.latitude != null && container.longitude != null;
  const updatedAt = new Date(container.updated_at).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const hasAlarms =
    container.fire_alarm ||
    container.full_alarm ||
    container.tilt_alarm ||
    container.battery_alarm;

  return (
    <Box
      sx={{
        ...(embedded
          ? {
              height: '100%',
              width: '100%',
            }
          : {
              position: 'absolute',
              top: 0,
              insetInlineEnd: 0,
              height: '100%',
              width: { xs: '100%', sm: 360, md: 400 },
              maxWidth: '100%',
            }),
        bgcolor: 'background.paper',
        zIndex: 1000,
        boxShadow: (t) => `-6px 0 20px ${alpha(t.palette.common.black, 0.15)}`,
        display: 'flex',
        flexDirection: 'column',
        animation: 'slideInPanel 0.25s ease-out',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          bgcolor: alpha(theme.palette.primary.main, 0.06),
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              flexShrink: 0,
            }}
          >
            <CapacityIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2, m: 0 }}>
              {t('containers.containerDetails')}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              title={displayName}
              sx={{ display: 'block', mt: 0.25 }}
            >
              {displayName}
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label={t('common.close')} sx={{ flexShrink: 0 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 2 }}>
        <Stack spacing={1.25} divider={<Divider flexItem sx={{ opacity: 0.5 }} />}>
          <EntityDetailRow icon={<StatusIcon />} label={t('containers.card.status')}>
            <Chip
              label={t(STATUS_LABEL_KEYS[container.status])}
              size="small"
              sx={statusChipSx(statusColor, 'sm')}
            />
          </EntityDetailRow>

          {container.material_type?.name && (
            <EntityDetailRow icon={<MaterialIcon />} label={t('containers.card.material')}>
              <Typography variant="body2" fontWeight={500} noWrap>
                {container.material_type.name}
              </Typography>
            </EntityDetailRow>
          )}

          {container.device_id && (
            <EntityDetailRow icon={<DeviceIcon />} label={t('containers.card.device')}>
              <Typography variant="body2" fontWeight={500} noWrap>
                {container.device_id}
              </Typography>
            </EntityDetailRow>
          )}

          {fillLevelKg != null && (
            <EntityDetailRow icon={<CurrentIcon />} label={t('containers.card.current')}>
              <Typography variant="body2" fontWeight={500}>
                {fillLevelKg} kg
              </Typography>
            </EntityDetailRow>
          )}

          {capacity != null && capacityUnit && (
            <EntityDetailRow icon={<CapacityIcon />} label={t('containers.card.capacity')}>
              <Typography variant="body2" fontWeight={500}>
                {capacity} {capacityUnit}
              </Typography>
            </EntityDetailRow>
          )}

          {fillRate != null && (
            <EntityDetailRow icon={<FillIcon />} label={t('containers.card.fillLevel')}>
              <Box sx={{ width: '100%', maxWidth: 160 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" fontWeight={600}>
                    {fillRate}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={fillRate}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    '& .MuiLinearProgress-bar': { borderRadius: 3 },
                  }}
                />
              </Box>
            </EntityDetailRow>
          )}

          {container.buffer != null && (
            <EntityDetailRow icon={<BufferIcon />} label={t('containers.card.buffer')}>
              <Typography variant="body2" fontWeight={500}>
                {container.buffer}%
              </Typography>
            </EntityDetailRow>
          )}

          <EntityDetailRow
            icon={hasCoords || locationLabel ? <LocationIcon /> : <LocationOffIcon />}
            label={t('containers.card.location')}
          >
            {locationLabel ? (
              <Typography variant="body2" fontWeight={500} noWrap title={locationLabel}>
                {locationLabel}
              </Typography>
            ) : hasCoords ? (
              <Typography variant="body2" fontWeight={500} noWrap>
                {container.latitude}, {container.longitude}
              </Typography>
            ) : (
              <Chip
                label={t('containers.card.locationNotSet')}
                size="small"
                sx={statusChipSx(STATUS_NEUTRAL, 'sm')}
              />
            )}
          </EntityDetailRow>

          <EntityDetailRow icon={<DateIcon />} label={t('containers.card.updated')}>
            <Typography variant="body2" fontWeight={500} noWrap>
              {updatedAt}
            </Typography>
          </EntityDetailRow>
        </Stack>

        {hasAlarms && (
          <Box sx={{ mt: 2.5 }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              {t('containers.map.activeAlarms')}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.75}>
              {container.fire_alarm && (
                <Chip icon={<FireIcon />} label={t('containers.map.alarm.fire')} size="small" color="error" />
              )}
              {container.full_alarm && (
                <Chip icon={<FullIcon />} label={t('containers.map.alarm.full')} size="small" color="warning" />
              )}
              {container.tilt_alarm && (
                <Chip icon={<TiltIcon />} label={t('containers.map.alarm.tilt')} size="small" color="info" />
              )}
              {container.battery_alarm && (
                <Chip icon={<BatteryIcon />} label={t('containers.map.alarm.battery')} size="small" />
              )}
            </Stack>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" spacing={1}>
          {helpRowActionsPhase === 'edit' ? (
            <SetupCoachmark
              open
              title={t('containers.editContainerHintTitle')}
              subtitle={t('containers.editContainerHint')}
              nextLabel={t('selfSetup.tourNext')}
              onNext={() => onHelpRowActionsNext?.()}
              onDismiss={() => onHelpRowActionsDismiss?.()}
              targetSx={{ flex: 1, display: 'block' }}
            >
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => onEdit(container)}
                fullWidth
                sx={{ textTransform: 'none' }}
              >
                {t('common.edit')}
              </Button>
            </SetupCoachmark>
          ) : (
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => onEdit(container)}
              sx={{ flex: 1, textTransform: 'none' }}
            >
              {t('common.edit')}
            </Button>
          )}
          {helpQrHint ? (
            <SetupCoachmark
              open
              title={t('containers.printQrHintTitle')}
              subtitle={t('containers.printQrHint')}
              nextLabel={t('selfSetup.tourDone')}
              onNext={() => onHelpQrDismiss?.()}
              onDismiss={() => onHelpQrDismiss?.()}
              targetSx={{ flex: 1, display: 'block' }}
            >
              <Button
                size="small"
                variant="outlined"
                startIcon={<QrCodeIcon />}
                onClick={() => onQr(container)}
                fullWidth
                sx={{ textTransform: 'none' }}
              >
                {t('common.qr')}
              </Button>
            </SetupCoachmark>
          ) : (
            <Button
              size="small"
              variant="outlined"
              startIcon={<QrCodeIcon />}
              onClick={() => onQr(container)}
              sx={{ flex: 1, textTransform: 'none' }}
            >
              {t('common.qr')}
            </Button>
          )}
          {helpRowActionsPhase === 'delete' ? (
            <SetupCoachmark
              open
              title={t('containers.deleteContainerHintTitle')}
              subtitle={t('containers.deleteContainerHint')}
              nextLabel={t('selfSetup.tourDone')}
              onNext={() => onHelpRowActionsDismiss?.()}
              onDismiss={() => onHelpRowActionsDismiss?.()}
              targetSx={{ flex: 1, display: 'block' }}
            >
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => onDelete(container.id)}
                fullWidth
                sx={{ textTransform: 'none' }}
              >
                {t('common.delete')}
              </Button>
            </SetupCoachmark>
          ) : (
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => onDelete(container.id)}
              sx={{ flex: 1, textTransform: 'none' }}
            >
              {t('common.delete')}
            </Button>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default ContainerMapDetailPanel;
